import { apiConfig, apiUrl } from './config'
import { session } from './session'

export class ApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string, readonly details?: unknown) {
    super(message)
  }
}

async function parse(res: Response): Promise<unknown> {
  const text = await res.text()
  if (!text) return null
  try { return JSON.parse(text) } catch { return text }
}

function toError(status: number, body: unknown): ApiError {
  const e = (body as { error?: { code?: string; message?: string; details?: unknown } } | null)?.error
  let message = e?.message ?? `HTTP ${status}`
  if (Array.isArray(e?.details) && e.details.length > 0) {
    const first = e.details[0] as { message?: string; path?: Array<string | number> }
    if (first?.message) {
      const field = Array.isArray(first.path) && first.path.length > 0 ? `${first.path.join('.')}: ` : ''
      message = `${field}${first.message}`
    }
  }
  return new ApiError(status, e?.code ?? 'INTERNAL', message, e?.details)
}

/** JSON request with the access token; one transparent refresh on 401. */
export async function request<T>(method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = {}
  const token = session.get().token
  if (token) headers.authorization = `Bearer ${token}`
  let payload: BodyInit | undefined
  if (body instanceof FormData) payload = body
  else if (body !== undefined) {
    headers['content-type'] = 'application/json'
    payload = JSON.stringify(body)
  }
  const res = await fetch(apiUrl(path), {
    method, headers, body: payload,
    credentials: apiConfig().canRefresh ? 'same-origin' : 'omit'
  })
  const data = await parse(res)
  if (res.status === 401 && token && !retried && apiConfig().canRefresh && (await session.refresh())) {
    return request<T>(method, path, body, true)
  }
  if (!res.ok) throw toError(res.status, data)
  return data as T
}

export const api = {
  get: <T>(p: string) => request<T>('GET', p),
  post: <T>(p: string, b?: unknown) => request<T>('POST', p, b ?? {}),
  patch: <T>(p: string, b: unknown) => request<T>('PATCH', p, b),
  del: <T>(p: string) => request<T>('DELETE', p)
}

/** Plain POST without the token (setup/login/register/refresh). */
export async function publicPost<T>(path: string, body: unknown): Promise<T> {
  const res = await fetch(apiUrl(path), {
    method: 'POST',
    credentials: apiConfig().canRefresh ? 'same-origin' : 'omit',
    headers: { 'content-type': 'application/json' }, body: JSON.stringify(body)
  })
  const data = await parse(res)
  if (!res.ok) throw toError(res.status, data)
  return data as T
}

export async function download(path: string, filename: string): Promise<void> {
  const token = session.get().token
  const res = await fetch(apiUrl(path), {
    headers: token ? { authorization: `Bearer ${token}` } : {},
    credentials: apiConfig().canRefresh ? 'same-origin' : 'omit'
  })
  if (!res.ok) throw toError(res.status, await parse(res))
  const url = URL.createObjectURL(await res.blob())
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  setTimeout(() => URL.revokeObjectURL(url), 10_000)
}
